"use client";

import { SparklesIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { SearchEngineTab } from "@/app/(home)/_components/search-engine-tab";
import LinkLoadingIndicator from "@/components/loader/link-loading-indicator";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { legalPageLinks } from "@/config/public-pages";
import { HeaderAuthActions } from "@/feature/auth/components/header-auth-actions";
import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";
import { Suspense, useState } from "react";
import { ModeSwitcher } from "../../../components/layout/mood-switcher";
import { MobileNav } from "./mobile-nav";

const desktopSupportLinks: Array<{ href: string; label: string }> = [
  { href: "/contact-us", label: "Support" },

  { href: "/blog", label: "Blog" },
  { href: "/about-us", label: "About Us" },
];

const policyMegaLinks = legalPageLinks.map((page) => ({
  href: page.href,
  title: page.label,
  description: page.description,
}));

type HeaderProps = {
  forceHomeNavigation?: boolean;
};

function PolicyMenuLink({
  href,
  title,
  description,
  active,
}: {
  href: string;
  title: string;
  description: string;
  active: boolean;
}) {
  return (
    <NavigationMenuLink asChild>
      <Link
        href={href}
        className={cn(
          "flex flex-col items-start gap-1 rounded-md p-3 transition-colors hover:bg-muted focus:bg-muted",
          active && "bg-muted/70",
        )}
      >
        <span
          className={cn(
            " font-medium leading-none",
            active && "text-foreground",
          )}
        >
          {title}
        </span>
        <span className=" text-muted-foreground">{description}</span>
      </Link>
    </NavigationMenuLink>
  );
}

export function Header({ forceHomeNavigation = false }: HeaderProps) {
  const pathname = usePathname();

  const [mobileNavPathname, setMobileNavPathname] = useState<string | null>(
    null,
  );
  const open = mobileNavPathname === pathname;
  const setOpen = (nextOpen: boolean) => {
    setMobileNavPathname(nextOpen ? pathname : null);
  };

  const showHomeNavigation = forceHomeNavigation || pathname === "/";
  const isRouteActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}`);
  const aiPlannerActive = pathname.startsWith("/ai-planner");
  const policiesMenuActive = legalPageLinks.some((page) =>
    isRouteActive(page.href),
  );

  const isSticky = true;

  return (
    <header
      className={cn("bg-background  top-0 z-50 w-full", {
        sticky: isSticky || open,
      })}
    >
      <div className="container-wrapper">
        <div className="container  px-0 flex h-(--header-height) items-center justify-between ">
          <MobileNav className="flex lg:hidden" open={open} setOpen={setOpen} />

          <Link
            href="/"
            className="hidden lg:flex items-center gap-2 overflow-hidden"
          >
            <Image
              src={"/T360.svg"}
              alt="Logo"
              width={432}
              height={432}
              className="size-16"
              priority
            />
          </Link>

          <div className="hidden md:flex md:flex-1 md:items-center md:justify-center">
            {showHomeNavigation ? (
              <nav className="flex items-center gap-1">
                {desktopSupportLinks.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "relative rounded-md px-3 py-2  font-medium transition-colors",
                      "after:absolute after:left-3 after:right-3 after:bottom-1 after:h-0.5 after:origin-left after:rounded-full after:bg-primary after:transition-transform after:duration-300 motion-reduce:after:transition-none",
                      isRouteActive(item.href)
                        ? "text-foreground after:scale-x-100"
                        : "text-muted-foreground hover:text-foreground after:scale-x-0 hover:after:scale-x-100",
                    )}
                  >
                    <LinkLoadingIndicator title={item.label} />
                  </Link>
                ))}

                <NavigationMenu>
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuTrigger
                        className={cn(
                          "relative text-md text-muted-foreground hover:text-foreground",
                          "after:absolute after:left-4 after:right-4 after:bottom-1 after:h-0.5 after:origin-left after:rounded-full after:bg-primary after:transition-transform after:duration-300 motion-reduce:after:transition-none hover:bg-transparent!",
                          policiesMenuActive
                            ? "text-foreground after:scale-x-100"
                            : "after:scale-x-0 data-open:after:scale-x-100 hover:after:scale-x-100",
                        )}
                      >
                        Policies
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <div className="grid gap-2 p-2 md:w-[560px] md:grid-cols-2">
                          {policyMegaLinks.map((item) => (
                            <PolicyMenuLink
                              key={item.href}
                              {...item}
                              active={isRouteActive(item.href)}
                            />
                          ))}
                        </div>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
              </nav>
            ) : (
              <Suspense fallback={<div className="h-10 w-full" />}>
                <SearchEngineTab variant="header" className="justify-center" />
              </Suspense>
            )}
          </div>

          <div className="items-center gap-2 flex  min-w-56">
            <ModeSwitcher />
            <Button
              asChild
              variant={aiPlannerActive ? "default" : "outline"}
              size="sm"
            >
              <Link href="/ai-planner">
                <SparklesIcon />
                AI
              </Link>
            </Button>
            <div className="flex-1 justify-end flex">
              <Suspense
                fallback={
                  <Button variant="outline" size="sm" disabled>
                    Account
                  </Button>
                }
              >
                <HeaderAuthActions />
              </Suspense>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
